import React, { useEffect, useRef, useCallback, useState } from 'react'
import styled from 'styled-components'
import { Transition } from 'react-transition-group'
import NavLink, { NavItemRow, NavItemPrefix } from './NavLink'
import SocialLinks from './SocialLinks'
import AccessibilityPanel from './AccessibilityPanel'
import AccessibilityIcon from './AccessibilityIcon'
import useReducedMotionPreference from '../../../hooks/useReducedMotionPreference'

const NAV_ITEMS = [
  { to: '/', label: 'Home', number: '01' },
  { to: '/blog', label: 'Blog', number: '02' },
  { to: '/talks', label: 'Talks', number: '03' },
  { to: '/links', label: 'Links', number: '04' },
  { to: '/about', label: 'About', number: '05' },
]

const STAGGER_DELAY = 80
const ENTER_DURATION = 400
const EXIT_DURATION = 300
const VIEW_CROSSFADE = 250

const DialogContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgb(var(--bg-color));
  overflow-y: auto;
  transition: opacity ${EXIT_DURATION}ms ease;
`

const ViewLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6rem 4.5rem;
  transition:
    opacity ${VIEW_CROSSFADE}ms ease,
    transform ${VIEW_CROSSFADE}ms ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '12px')});

  @media screen and (min-width: 880px) {
    padding: 12rem;
  }
`

const NavList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`

const Footer = styled.div`
  margin-top: auto;
  padding-top: 2rem;
`

const Divider = styled.li`
  list-style: none;
  height: 1px;
  background: rgba(var(--text-color), 0.15);
  margin: 1.5rem 0 1rem;
  padding: 0;
  max-width: 18rem;

  @media screen and (max-width: 480px) {
    max-width: none;
  }
`

const NavActionButton = styled.button`
  font-family: 'Merriweather', serif;
  font-weight: 300;
  font-size: 1.4rem;
  line-height: 1.3;
  color: rgba(var(--text-color), 0.7);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  display: inline-block;
  transition:
    transform 0.3s ease,
    color 0.3s ease;

  &:hover {
    color: rgb(var(--tltr-awesome-value));
    transform: translateX(1rem);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--tltr-awesome-value));
    outline-offset: 4px;
    border-radius: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const IconPrefix = styled(NavItemPrefix)`
  color: rgba(var(--text-color), 0.7);

  svg {
    display: block;
  }
`

const overlayTransitionStyles = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
}

const linkTransitionStyles = (index, state, reducedMotion) => {
  const isEntering = state === 'entering' || state === 'entered'

  if (reducedMotion) {
    return { opacity: isEntering ? 1 : 0 }
  }

  return isEntering
    ? {
        opacity: 1,
        transform: 'translateY(0)',
        transition: `opacity ${ENTER_DURATION}ms ease ${index * STAGGER_DELAY}ms, transform ${ENTER_DURATION}ms ease ${index * STAGGER_DELAY}ms`,
      }
    : {
        opacity: 0,
        transform: 'translateY(20px)',
      }
}

const Overlay = ({ isOpen, onClose, buttonRef }) => {
  const overlayRef = useRef(null)
  const nodeRef = useRef(null)
  const menuViewRef = useRef(null)
  const a11yViewRef = useRef(null)
  const [view, setView] = useState('menu')
  const reducedMotion = useReducedMotionPreference()

  // Reset to the menu view whenever the overlay closes so reopening
  // always starts fresh.
  useEffect(() => {
    if (!isOpen) setView('menu')
  }, [isOpen])

  // Mark the inactive ViewLayer as `inert` so the browser skips its
  // focusable descendants in tab order and hides them from assistive
  // tech. Defensive: the manual focus trap below also filters by layer,
  // but `inert` ensures that even if focus somehow lands in the hidden
  // layer, the browser rejects it.
  useEffect(() => {
    if (!isOpen) return
    if (menuViewRef.current) menuViewRef.current.inert = view !== 'menu'
    if (a11yViewRef.current)
      a11yViewRef.current.inert = view !== 'accessibility'
  }, [isOpen, view])

  // Focus trap limited to the currently visible view. The `inert`
  // attribute above handles most cases, but we keep this manual trap
  // so Tab wraps cleanly at the boundaries (from last focusable back to
  // the hamburger button) rather than escaping to browser chrome.
  const handleFocusTrap = useCallback(
    (e) => {
      if (e.key !== 'Tab') return
      const activeLayer =
        view === 'menu' ? menuViewRef.current : a11yViewRef.current
      if (!activeLayer) return

      const focusableInLayer = Array.from(
        activeLayer.querySelectorAll(
          'a[href], button, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      const focusable = [buttonRef?.current, ...focusableInLayer].filter(
        Boolean
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    [buttonRef, view]
  )

  useEffect(() => {
    if (!isOpen) return

    document.addEventListener('keydown', handleFocusTrap)

    const button = buttonRef?.current
    return () => {
      document.removeEventListener('keydown', handleFocusTrap)
      button?.focus()
    }
  }, [isOpen, handleFocusTrap, buttonRef])

  const handleEntered = useCallback(() => {
    // Focus first focusable element of the active view.
    const layer = view === 'menu' ? menuViewRef.current : a11yViewRef.current
    layer?.querySelector('a[href], button')?.focus()
  }, [view])

  // When the view changes (while overlay is open), move focus to the
  // first focusable in the newly visible layer after the crossfade
  // starts so tab order is correct.
  useEffect(() => {
    if (!isOpen) return
    const layer = view === 'menu' ? menuViewRef.current : a11yViewRef.current
    // Let the crossfade begin painting first, then focus.
    const id = requestAnimationFrame(() => {
      layer?.querySelector('a[href], button')?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [view, isOpen])

  const handleOpenAccessibility = useCallback(
    () => setView('accessibility'),
    []
  )
  const handleBackToMenu = useCallback(() => setView('menu'), [])

  return (
    <Transition
      in={isOpen}
      timeout={{ enter: ENTER_DURATION, exit: EXIT_DURATION }}
      unmountOnExit
      nodeRef={nodeRef}
      onEntered={handleEntered}
    >
      {(state) => (
        <DialogContainer
          ref={(el) => {
            nodeRef.current = el
            overlayRef.current = el
          }}
          id="nav-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          style={overlayTransitionStyles[state]}
        >
          <ViewLayer
            ref={menuViewRef}
            $visible={view === 'menu'}
            aria-hidden={view !== 'menu'}
          >
            <nav aria-label="Site navigation">
              <NavList>
                {NAV_ITEMS.map((item, index) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    number={item.number}
                    onClick={onClose}
                    style={linkTransitionStyles(index, state, reducedMotion)}
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Divider role="separator" aria-hidden="true" />
                <NavItemRow
                  style={linkTransitionStyles(
                    NAV_ITEMS.length,
                    state,
                    reducedMotion
                  )}
                >
                  <IconPrefix aria-hidden="true">
                    <AccessibilityIcon size={20} />
                  </IconPrefix>
                  <NavActionButton
                    type="button"
                    onClick={handleOpenAccessibility}
                  >
                    Accessibility
                  </NavActionButton>
                </NavItemRow>
              </NavList>
            </nav>
            <Footer>
              <SocialLinks />
            </Footer>
          </ViewLayer>

          <ViewLayer
            ref={a11yViewRef}
            $visible={view === 'accessibility'}
            aria-hidden={view !== 'accessibility'}
          >
            <AccessibilityPanel onBack={handleBackToMenu} />
          </ViewLayer>
        </DialogContainer>
      )}
    </Transition>
  )
}

export default Overlay
